/**
 * Preferences Context
 * Provides team preferences (table columns, etc.) to components
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import PreferencesService from 'shared/services/preferencesService';
import { DEFAULT_COLUMNS } from 'features/activity/config/eventColumns';
import { DEFAULT_COLUMNS as DEFAULT_SESSIONS_COLUMNS } from 'features/activity/config/sessionColumns';
import { DEFAULT_COLUMNS as DEFAULT_USERS_COLUMNS } from 'features/activity/config/userColumns';
import { DEFAULT_GROUP_COLUMNS } from 'features/activity/config/groupColumns';
import { DEFAULT_RUN_COLUMN_KEYS } from 'features/agent-analytics/utils/runColumns';

const DEFAULT_AGENT_RUNS_COLUMNS = {
  enabled: DEFAULT_RUN_COLUMN_KEYS,
  order: DEFAULT_RUN_COLUMN_KEYS,
};

const PreferencesContext = createContext(null);

export const PreferencesProvider = ({ children }) => {
  const [eventsTableColumns, setEventsTableColumns] = useState(null);
  const [sessionsTableColumns, setSessionsTableColumns] = useState(null);
  const [usersTableColumns, setUsersTableColumns] = useState(null);
  const [agentRunsTableColumns, setAgentRunsTableColumns] = useState(null);
  // Groups: keyed by group_key string → column config
  const [groupsTableColumns, setGroupsTableColumns] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Load events table columns for a team
   */
  const loadEventsTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setEventsTableColumns(DEFAULT_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const columns = await PreferencesService.getEventsTableColumns(teamId);
      setEventsTableColumns(columns);
    } catch (err) {
      console.error('Error loading events table columns:', err);
      setError(err.message);
      // Fallback to default columns
      setEventsTableColumns(DEFAULT_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update events table columns for a team
   */
  const updateEventsTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);
    
    try {
      const updatedColumns = await PreferencesService.updateEventsTableColumns(teamId, enabled, order);
      setEventsTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating events table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset events table columns to default
   */
  const resetEventsTableColumns = useCallback(async (teamId) => {
    return updateEventsTableColumns(teamId, DEFAULT_COLUMNS.enabled, DEFAULT_COLUMNS.order);
  }, [updateEventsTableColumns]);

  /**
   * Get current events table columns (with fallback to default)
   */
  const getEventsTableColumns = useCallback(() => {
    return eventsTableColumns || DEFAULT_COLUMNS;
  }, [eventsTableColumns]);

  /**
   * Load sessions table columns for a team
   */
  const loadSessionsTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setSessionsTableColumns(DEFAULT_SESSIONS_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const columns = await PreferencesService.getSessionsTableColumns(teamId);
      setSessionsTableColumns(columns);
    } catch (err) {
      console.error('Error loading sessions table columns:', err);
      setError(err.message);
      setSessionsTableColumns(DEFAULT_SESSIONS_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update sessions table columns for a team
   */
  const updateSessionsTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatedColumns = await PreferencesService.updateSessionsTableColumns(teamId, enabled, order);
      setSessionsTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating sessions table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset sessions table columns to default
   */
  const resetSessionsTableColumns = useCallback(async (teamId) => {
    return updateSessionsTableColumns(teamId, DEFAULT_SESSIONS_COLUMNS.enabled, DEFAULT_SESSIONS_COLUMNS.order);
  }, [updateSessionsTableColumns]);

  /**
   * Get current sessions table columns (with fallback to default)
   */
  const getSessionsTableColumns = useCallback(() => {
    return sessionsTableColumns || DEFAULT_SESSIONS_COLUMNS;
  }, [sessionsTableColumns]);

  /**
   * Load users table columns for a team
   */
  const loadUsersTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setUsersTableColumns(DEFAULT_USERS_COLUMNS);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const columns = await PreferencesService.getUsersTableColumns(teamId);
      setUsersTableColumns(columns);
    } catch (err) {
      console.error('Error loading users table columns:', err);
      setError(err.message);
      setUsersTableColumns(DEFAULT_USERS_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update users table columns for a team
   */
  const updateUsersTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) {
      throw new Error('Team ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const updatedColumns = await PreferencesService.updateUsersTableColumns(teamId, enabled, order);
      setUsersTableColumns(updatedColumns);
      return updatedColumns;
    } catch (err) {
      console.error('Error updating users table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Reset users table columns to default
   */
  const resetUsersTableColumns = useCallback(async (teamId) => {
    return updateUsersTableColumns(teamId, DEFAULT_USERS_COLUMNS.enabled, DEFAULT_USERS_COLUMNS.order);
  }, [updateUsersTableColumns]);

  /**
   * Get current users table columns (with fallback to default)
   */
  const getUsersTableColumns = useCallback(() => {
    return usersTableColumns || DEFAULT_USERS_COLUMNS;
  }, [usersTableColumns]);

  /**
   * Get group table columns for a specific group_key (stored in localStorage per team+key)
   */
  const getGroupsTableColumns = useCallback((groupKey) => {
    const key = groupKey || 'default';
    if (groupsTableColumns[key]) return groupsTableColumns[key];
    // Try localStorage fallback
    try {
      const stored = localStorage.getItem(`groups_columns_${key}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed;
      }
    } catch (_) {}
    return DEFAULT_GROUP_COLUMNS;
  }, [groupsTableColumns]);

  const updateGroupsTableColumns = useCallback(async (groupKey, enabled, order) => {
    const key = groupKey || 'default';
    const config = { enabled, order };
    setGroupsTableColumns(prev => ({ ...prev, [key]: config }));
    try {
      localStorage.setItem(`groups_columns_${key}`, JSON.stringify(config));
    } catch (_) {}
    return config;
  }, []);

  const resetGroupsTableColumns = useCallback(async (groupKey) => {
    const key = groupKey || 'default';
    setGroupsTableColumns(prev => ({ ...prev, [key]: DEFAULT_GROUP_COLUMNS }));
    try {
      localStorage.removeItem(`groups_columns_${key}`);
    } catch (_) {}
    return DEFAULT_GROUP_COLUMNS;
  }, []);

  // ---- Agent runs table columns ---- //
  const loadAgentRunsTableColumns = useCallback(async (teamId) => {
    if (!teamId) {
      setAgentRunsTableColumns(DEFAULT_AGENT_RUNS_COLUMNS);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const columns = await PreferencesService.getAgentRunsTableColumns(teamId);
      setAgentRunsTableColumns(columns);
    } catch (err) {
      console.error('Error loading agent runs table columns:', err);
      setError(err.message);
      setAgentRunsTableColumns(DEFAULT_AGENT_RUNS_COLUMNS);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAgentRunsTableColumns = useCallback(async (teamId, enabled, order) => {
    if (!teamId) throw new Error('Team ID is required');
    setLoading(true);
    setError(null);
    try {
      const updated = await PreferencesService.updateAgentRunsTableColumns(teamId, enabled, order);
      setAgentRunsTableColumns(updated);
      return updated;
    } catch (err) {
      console.error('Error updating agent runs table columns:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const resetAgentRunsTableColumns = useCallback(async (teamId) => (
    updateAgentRunsTableColumns(teamId, DEFAULT_AGENT_RUNS_COLUMNS.enabled, DEFAULT_AGENT_RUNS_COLUMNS.order)
  ), [updateAgentRunsTableColumns]);

  const getAgentRunsTableColumns = useCallback(() => (
    agentRunsTableColumns || DEFAULT_AGENT_RUNS_COLUMNS
  ), [agentRunsTableColumns]);

  const value = {
    // State
    eventsTableColumns: getEventsTableColumns(),
    agentRunsTableColumns: getAgentRunsTableColumns(),
    loading,
    error,

    // Actions
    loadEventsTableColumns,
    updateEventsTableColumns,
    resetEventsTableColumns,
    getEventsTableColumns,
    loadSessionsTableColumns,
    updateSessionsTableColumns,
    resetSessionsTableColumns,
    getSessionsTableColumns,
    loadUsersTableColumns,
    updateUsersTableColumns,
    resetUsersTableColumns,
    getUsersTableColumns,
    loadAgentRunsTableColumns,
    updateAgentRunsTableColumns,
    resetAgentRunsTableColumns,
    getAgentRunsTableColumns,
    getGroupsTableColumns,
    updateGroupsTableColumns,
    resetGroupsTableColumns
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

/**
 * Hook to use preferences context
 */
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within PreferencesProvider');
  }
  return context;
};

export default PreferencesContext;
